import React, { useEffect, useState, useMemo, useCallback, useRef, lazy, Suspense } from 'react'
import Head from 'next/head'
import allAlerts from '@/config/alerts.json'
import { GetServerSideProps } from 'next'
import { ParsedUrlQuery } from 'querystring'
import GetBySlug from '@/services/api/products/GetBySlug'
import { useRouter } from 'next/router'
import Link from 'next/link'
import toast from 'react-hot-toast'
import CustomToast from '@/components/CustomToast'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  GetReviews,
  ReviewDelete,
  ReviewPost,
  ReviewUpdate,
} from '@/services/api/review'
import useAuth from '@/hooks/useAuth'
import GetRelatedProducts from '@/services/api/products/GetRelatedProducts'
import {
  CheckInWishlist,
  WishlistAdd,
  WishlistDelete,
} from '@/services/api/wishlist'
import { GetSettings } from '@/services/api/settings'
import { GetDocumentInputs } from '@/services/api/personalize/GetDocuments'
import { useLoginModal } from '@/contexts/LoginModalContext'
import { usePersonalizeModal } from '@/contexts/PersonalizeModalContext'
import { useAIPersonalizeModal } from '@/contexts/AIPersonalizeModalContext'
import { useCTAContext } from '@/contexts/CTAContext'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import useUserPreferences from '@/hooks/useUserPreferences'
import { track } from '@/lib/tracker'

// Lazy load view components
const DesktopView = lazy(() => import('@/components/Product/DesktopView'))
const MobileProductSection = lazy(() => import('@/components/Product/MobileView'))

interface IParams extends ParsedUrlQuery {
  slug: string
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { slug } = context.params as IParams
  const locale = context.locale ?? 'en'

  try {
    const cookies = context.req.headers.cookie || ''
    const cookieArray = cookies.split(';').map(cookie => cookie.trim())

    let userCountry = 'IL'
    let userCurrency = 'ILS'

    cookieArray.forEach(cookie => {
      const [key, value] = cookie.split('=')
      if (key === 'userCountry') userCountry = value || 'IL'
      else if (key === 'userCurrency') userCurrency = value || 'ILS'
    })

    const [staticProduct, settings, inputs] = await Promise.all([
      GetBySlug(slug, userCurrency, userCountry),
      GetSettings(userCountry, userCurrency),
      GetDocumentInputs(slug),
    ])

    return {
      props: {
        ...(await serverSideTranslations(locale, [
          'products',
          'productsHeroBanner',
          'categoryTabs',
          'filterModal',
          'filterSidebar',
          'desktopHeader',
          'footer',
          'common',
          'account',
          'alerts',
          'address',
          'cart',
          'contactus',
          'deliveryPolicy',
          'notfound',
          'payment',
          'personalizeModal',
          'product',
          'removePhone',
        ])),
        staticProduct,
        settings,
        inputs,
        userCountry,
        userCurrency,
      },
    }
  } catch (error) {
    console.error('Error fetching product data:', error)
    return {
      props: {
        ...(await serverSideTranslations(locale, [
          'desktopHeader',
          'footer',
          'product',
        ])),
        staticProduct: null,
        settings: null,
        inputs: null,
        userCountry: 'IL',
        userCurrency: 'ILS',
      },
    }
  }
}

interface Props {
  staticProduct: any
  settings: any
  inputs: any
  userCountry: string
  userCurrency: string
}

// Loading skeleton for lazy components
const ProductViewSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-96 bg-gray-200" />
    <div className="h-64 bg-gray-100 mt-4" />
  </div>
)

export default function Product({ staticProduct, settings: staticSettings, inputs, userCountry, userCurrency }: Props) {
  const preferences = useUserPreferences()
  const [product, setProduct] = useState(staticProduct)
  const [settings, setSettings] = useState(staticSettings)
  const [isRefetching, setIsRefetching] = useState(false)

  // Fetch product with user's currency preference
  useEffect(() => {
    const fetchProductWithUserCurrency = async () => {
      if (!preferences?.currencyCode || !staticProduct?.slug) return

      const userCurrencyPref = preferences.currencyCode
      const userCountryPref = preferences.countryCode || 'IL'

      if (userCurrencyPref !== userCurrency || userCountryPref !== userCountry) {
        setIsRefetching(true)
        try {
          const [updatedProduct, updatedSettings] = await Promise.all([
            GetBySlug(staticProduct.slug, userCurrencyPref, userCountryPref),
            GetSettings(userCountryPref, userCurrencyPref),
          ])
          setProduct(updatedProduct)
          setSettings(updatedSettings)
        } catch (error) {
          console.error('Error fetching product with user currency:', error)
        } finally {
          setIsRefetching(false)
        }
      } else {
        setProduct(staticProduct)
        setSettings(staticSettings)
      }
    }

    fetchProductWithUserCurrency()
  }, [preferences?.currencyCode, preferences?.countryCode, staticProduct?.slug, staticSettings, userCountry, userCurrency])

  const { t } = useTranslation('product')
  const { t: tAlerts } = useTranslation('alerts')
  const { openModal } = usePersonalizeModal()
  const { openAIModal } = useAIPersonalizeModal()
  const router = useRouter()
  const { locale } = router

  // Track product view
  useEffect(() => {
    if (!product?.id) return
    track({
      event_name: 'view_product',
      metadata: { product_id: product.id, product_name: product.product_name },
    })
  }, [product?.id])

  // Derived values with useMemo
  const name: string = useMemo(
    () => (router.query.name as string) || (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en) || '',
    [router.query.name, locale, product?.replace_name]
  )
  const gender = useMemo(() => (router.query.gender as string) || '', [router.query.gender])
  // Fixed: use as const to infer literal type
  const productGender = useMemo(() => product?.has_female ? 'Female' : 'Male' as 'Female' | 'Male', [product?.has_female])
  const DemoName = useMemo(() => (locale === 'ar' ? product?.demo_name?.ar : product?.demo_name?.en), [locale, product?.demo_name])

  // Review state
  const [reviewStar, setReviewStar] = useState(0)
  const [review, setReview] = useState('')
  const [isEditing, setIsEditing] = useState<undefined | number>(undefined)
  const [wordCount, setWordCount] = useState(0)
  const [charCount, setCharCount] = useState(0)

  // Queries
  const { data: reviews, isLoading: reviewsLoading } = useQuery(['reviews', product?.id], GetReviews, { enabled: !!product?.id })
  const { user } = useAuth('none')
  const { setIsOpen } = useLoginModal()

  // Mutations
  const mutation = useMutation(ReviewPost)
  const DeleteMutation = useMutation(ReviewDelete)
  const UpdateMutation = useMutation(ReviewUpdate)
  const WishlistAddMutation = useMutation(WishlistAdd)
  const DeleteWishlistMutation = useMutation(WishlistDelete)

  const queryClient = useQueryClient()
  const alerts = allAlerts[locale as keyof typeof allAlerts] || allAlerts.ar

  const country = preferences?.countryCode
  const currency = preferences?.currencyCode

  // Related products query
  const { data: related_products } = useQuery(
    [
      'related_products',
      {
        category_id: product?.categories?.map((cat: any) => cat.id).toString() || '',
        product_id: product?.id,
        gender,
        country,
        currency,
      },
    ],
    GetRelatedProducts,
    { enabled: !!product?.id && !!country && !!currency }
  )

  // Wishlist check query
  const checkInWishlistQuery = useQuery(['check_in_wishlist', product?.id], CheckInWishlist, { enabled: !!product?.id })

  // CTA visibility
  const ctaRef = useRef<HTMLDivElement>(null)
  const { setCTAVisible } = useCTAContext()

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setCTAVisible(entry.isIntersecting),
      { threshold: 0.02 }
    )
    const currentElement = ctaRef.current
    if (currentElement) observer.observe(currentElement)
    return () => {
      if (currentElement) observer.unobserve(currentElement)
    }
  }, [setCTAVisible])

  // Build input values for personalization
  const BuildInputValues = useCallback(() => {
    const build_inputs: { [key: string]: string } = {}
    if (!inputs) return build_inputs
    inputs.forEach((input: string) => {
      if (input === 'name') build_inputs[input] = name
      else if (input === 'first_name') build_inputs[input] = name.split(' ')[0]
      else if (input === 'init') build_inputs[input] = name.charAt(0)
      else if (input === 'age') build_inputs[input] = ''
    })
    return build_inputs
  }, [inputs, name])

  // Personalize handler
  const handlePersonalizeClick = useCallback(() => {
    if (!product) return
    if (product.is_ai === 1) {
      openAIModal({ product, inputs: BuildInputValues() })
    } else {
      openModal({ product, inputs: BuildInputValues() })
    }
  }, [product, BuildInputValues, openAIModal, openModal])

  // Review handlers
  const handleCreate = useCallback(() => {
    if (!product?.id) return
    mutation.mutate(
      { id: product.id, data: { stars: reviewStar, review } },
      {
        onSuccess() {
          queryClient.invalidateQueries(['reviews'])
          setReview('')
          setReviewStar(0)
          toast.custom((t) => <CustomToast type="success" t={t} message={tAlerts('review_added')} />, { duration: 5000 })
        },
        onError(error: any) {
          toast.custom((t) => <CustomToast type="error" t={t} message={error.response.data.message} />, { duration: 4000, id: 'Validation Error' })
        },
      }
    )
  }, [product?.id, reviewStar, review, mutation, queryClient, tAlerts])

  const handleUpdate = useCallback(() => {
    if (!isEditing) return
    UpdateMutation.mutate(
      { id: isEditing, data: { stars: reviewStar, review } },
      {
        onSuccess() {
          queryClient.invalidateQueries(['reviews'])
          setReview('')
          setReviewStar(0)
          setIsEditing(undefined)
          toast.custom((t) => <CustomToast type="success" t={t} message={tAlerts('review_updated')} />, { duration: 5000 })
        },
        onError(error: any) {
          toast.custom((t) => <CustomToast type="error" t={t} message={error.response.data.message} />, { duration: 4000, id: 'Validation Error' })
        },
      }
    )
  }, [isEditing, reviewStar, review, UpdateMutation, queryClient, tAlerts])

  const deleteReview = useCallback((id: number) => {
    DeleteMutation.mutate(id, { onSuccess: () => queryClient.invalidateQueries(['reviews']) })
  }, [DeleteMutation, queryClient])

  const editReview = useCallback((id: number, stars: number, reviewText: string) => {
    setReviewStar(stars)
    setReview(reviewText)
    setIsEditing(id)
  }, [])

  // Wishlist handlers
  const addToWishlist = useCallback((id: number) => {
    if (!user) {
      toast.error(alerts.login_wishlist)
      return
    }
    WishlistAddMutation.mutate(id, {
      onSuccess: () => {
        queryClient.invalidateQueries(['wishlist'])
        queryClient.invalidateQueries(['check_in_wishlist'])
        toast.success(alerts.wishlist_add, { duration: 5000 })
      },
      onError(error: any) {
        toast.error(error.response.data.message, { duration: 4000, id: 'Validation Error' })
      },
    })
  }, [user, alerts, WishlistAddMutation, queryClient])

  const WishlistDel = useCallback((id: number) => {
    DeleteWishlistMutation.mutate(id, {
      onSuccess: () => {
        queryClient.invalidateQueries(['wishlist'])
        queryClient.invalidateQueries(['check_in_wishlist'])
        toast.error(alerts.wishlist_remove, { duration: 5000 })
      },
      onError(error: any) {
        toast.error(error.response.data.message, { duration: 4000, id: 'Validation Error' })
      },
    })
  }, [alerts, DeleteWishlistMutation, queryClient])

  // Word count effect
  useEffect(() => {
    if (review) {
      const words = review.split(' ').filter(w => w.trim() !== '')
      setWordCount(words.length)
      setCharCount(review.length)
    } else {
      setWordCount(0)
      setCharCount(0)
    }
  }, [review])

  // Scroll related products
  const scrollRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollLeft = 0
  }, [related_products])

  // Average review stars
  const averageReviewStars = useMemo(() => {
    if (!reviews?.length) return 0
    return reviews.reduce((acc: any, review: any) => acc + review.stars, 0) / reviews.length
  }, [reviews])

  // Show loading while refetching
  if (isRefetching) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{t('applyingChanges')}</p>
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">{t('productNotAvailable')}</h1>
          <p>{t('productNotAvailableSorry')}</p>
          <Link href="/products" className="text-blue-500 hover:underline mt-4 inline-block">
            {t('productNotAvailableRetun')}
          </Link>
        </div>
      </div>
    )
  }

  // Shared props for both views (excluding component-specific ones)
  const sharedProps = {
    product,
    name,
    gender,
    productGender,
    reviews,
    averageReviewStars,
    settings,
    handlePersonalizeClick,
    check_in_wishlist: checkInWishlistQuery.data,
    WishlistDel,
    user,
    reviewStar,
    review,
    setReview,
    setReviewStar,
    wordCount,
    isEditing,
    setIsEditing,
    handleCreate,
    handleUpdate,
    mutation,
    UpdateMutation,
    DeleteMutation,
    related_products,
    addToWishlist,
    editReview,
    deleteReview,
  }

  return (
    <>
      <Head>
        <title>
          {DemoName
            ? DemoName.replace('{basmti}', name ?? (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en))
            : 'منتج - Basmti'}
        </title>
        <meta name="description" content="Basmti Project" />
      </Head>
      <main className="bg-white pb-24">
        <Suspense fallback={<ProductViewSkeleton />}>
          <MobileProductSection
            {...sharedProps}
            scrollRef={scrollRef}
            ctaRef={ctaRef}
          />
        </Suspense>
        <Suspense fallback={<ProductViewSkeleton />}>
          <DesktopView
            {...sharedProps}
            isLoading={reviewsLoading}
          />
        </Suspense>
      </main>
    </>
  )
}